import React, { Fragment, useState, useContext, useEffect, useRef, useCallback } from 'react';
import styles from './styles';
import { useApolloClient, useMutation } from '@apollo/react-hooks';
import {
  GET_STUDY_PROJECT,
  CREATE_STUDY_PROJECT_RESULT,
  GET_STUDENT_COURSE_PROGRESS,
  CREATE_RESULT_HISTORY,
  CREATE_STUDY_PROJECT_PROGRESS_DRAFT,
  GET_STUDY_PROJECT_PROGRESS_DRAFT,
} from '../../apollo/queries';
import { Box, Typography, Toolbar, Container, Modal, Button, Card } from '@material-ui/core';
import { CloseButton, LoadingScreen } from '../../components';
import {
  Start,
  Intro,
  Assessment,
  Results,
  StudyProjectComplete, 
} from './screens';
import { checkAnswer, removeTextFromUserAnswer, shuffle } from '../../lib';
import { ViewerContext } from '../../providers/ViewerProvider';
import { withSnackbar } from 'notistack';
import { UserConfirmationContext } from '../../providers/UserConfirmationProvider';
import { clone } from 'ramda';
import { formatAndShuffleReinforcedQuestions } from './helpers';
import AnswerBar from './components/AnswerBar';
import { AnswerResponse } from './components';
import { suffleSequenceAnswers } from '../Benchmark/helpers';

const StudyProject = ({ match, history, location, enqueueSnackbar }) => {
  const courseId = match.params.courseId;
  const groupId = match.params.groupId;
  const projectId = match.params.projectId;
  const studyProjectNumber = location.state.studyProjectNumber || 0;
  const preview = location.state.preview || match.url.split('/').includes('preview');

  const client = useApolloClient();
  const classes = styles();

  const scrollContainer = useRef(null);

  const { showConfirmation } = useContext(UserConfirmationContext);
  const { viewer } = useContext(ViewerContext);

  const [start, setStart] = useState(false);
  const [lastQuestion, setLastQuestion] = useState(false);
  const [studentAnswers, setStudentAnswers] = useState({});
  const [questionIndex, setQuestionIndex] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [caseStudyIndexes, setCaseStudyIndexes] = useState({});
  /**
   * State to save the data project/exam
   * @type {state}
   */
  const [project, setProject] = useState(null);
  const [showIntro, setShowIntro] = useState(false);
  const [showAssessment, setShowAssessment] = useState(false);
  const [studentResults, setStudentResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [correctAnswer, setCorrectAnswer] = useState(false);
  const [incorrectAnswer, setIncorrectAnswer] = useState(false);
  const [incorrectAnswerCount, setIncorrectAnswerCount] = useState(0);
  const [answerResponse, setAnswerResponse] = useState(null);
  const [scrollDownNotification, setScrollDownNotification] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openSaveProgressModal, setOpenSaveProgressModal] = useState(false);

  //Mutations
  const [createResults] = useMutation(CREATE_STUDY_PROJECT_RESULT);
  const [ createResultHistory ] = useMutation(CREATE_RESULT_HISTORY, {
    onError: (error) => {
      enqueueSnackbar('Oops! Something went wrong!', { variant: 'error' });
    },
  });

  const [createStudyProjectProgressDraft] = useMutation(CREATE_STUDY_PROJECT_PROGRESS_DRAFT, {
    onCompleted: (data) => {
      setOpenSaveProgressModal(true);
    },
    onError: (error) => {
      enqueueSnackbar('Oops! Something went wrong!', { variant: 'error' });
    }
  });

  //This useEffect show the scrollDown notification at bottom of the viewport
  useEffect(() => {
    const time = setTimeout(() => {
      if (!isScrolled) {
        if (scrollContainer.current?.scrollHeight > scrollContainer.current?.offsetHeight) {
          setScrollDownNotification(true);
        } else {
          setScrollDownNotification(false);
        }
      } else {
        setScrollDownNotification(false);
      }
    }, 500);

    return () => {
      clearTimeout(time);
    }
  });

  const handleOnShowAssessment = useCallback(() => {
    setShowIntro(false);
    setShowAssessment(true);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const result = await client.query({
          query: GET_STUDY_PROJECT,
          variables: { projectId },
        });

        const project = result.data.studyProject;

        //If preview is false, try to get progress draft and use it
        if (!preview) {
          const result2 = await client.query({
            query: GET_STUDY_PROJECT_PROGRESS_DRAFT,
            variables: {
              where: {
                user_id: viewer.id,
                study_project_id: projectId,
                group_id: groupId
              }
            },
            fetchPolicy: 'network-only'
          });
  
          const studyProjectProgressDraft = result2.data.studyProjectProgressDraft;
  
          if (studyProjectProgressDraft) {
            setStudentAnswers(JSON.parse(studyProjectProgressDraft.progress));
            const questionsId = JSON.parse(studyProjectProgressDraft.questions);
  
            
  
            const questions = project.caseStudies
            .filter((cs) => cs.published)
            .reduce(formatAndShuffleReinforcedQuestions, []);
  
            if (studyProjectProgressDraft.question_index > questions.length - 1) {
              setQuestionIndex(questions.length - 1);
            } else {
              setQuestionIndex(studyProjectProgressDraft.question_index);
            }
            
            const questionsInDraft = questions.reduce((acc, question) => {
              const questionInDraft = questionsId.findIndex((id) => id === question.id);
              if (questionInDraft !== -1) {
                acc[0][questionInDraft] = question;
              } else {
                acc[1].push(question);
              }
  
              return acc;
            }, [[], []]);
  
            const allQuestions = questionsInDraft[0].concat(questionsInDraft[1]).filter(element => element);
  
            allQuestions.forEach((question) => {
              if (question.type === 'SEQUENCE') {
                question['answers'] = suffleSequenceAnswers(question);
              } else {
                shuffle(question.answers);
              }
            });
  
            if (allQuestions.length === 1) {
              setLastQuestion(true);
            }
            
            setQuestions(allQuestions);
  
          } else {
            const questions = project.caseStudies
              .filter((cs) => cs.published)
              .reduce(formatAndShuffleReinforcedQuestions, []);
            questions.forEach((question) => {
              if (question.type === 'SEQUENCE') {
                question['answers'] = suffleSequenceAnswers(question);
              } else {
                shuffle(question.answers);
              }
            });
  
            if (questions.length === 1) {
              setLastQuestion(true);
            }
  
            setQuestions(questions);
          }
        } else {
          const questions = project.caseStudies
            .filter((cs) => cs.published)
            .reduce(formatAndShuffleReinforcedQuestions, []);
          questions.forEach((question) => {
            if (question.type === 'SEQUENCE') {
              question['answers'] = suffleSequenceAnswers(question);
            } else {
              shuffle(question.answers);
            }
          });

          if (questions.length === 1) {
            setLastQuestion(true);
          }

          setQuestions(questions);
        }

        const caseIndexes = project.caseStudies
        .filter((cs) => cs.published)
        .reduce((acc, caseStudy, index) => {
          acc[caseStudy.case.id] = index + 1;
          caseStudy.reinforcedQuestions.forEach((question) => {
            acc[question.id] = index + 1;
          });
          return acc;
        }, {});

        setCaseStudyIndexes(caseIndexes);
        setProject(project);
        setLoading(false);
      } catch (error) {
        setError('Could Not Find Study Project');
        setLoading(false);
      }
    })();
  }, [client, projectId, groupId, viewer, preview]);

  const checkUserAnswer = () => {
    setStudentAnswer(); // used for sequence questions
    const question = questions[questionIndex];

    if (checkAnswer(question, studentAnswers[question.id])) {
      setCorrectAnswer(true);
      setAnswerResponse({ case: 'correct', message: question.correct_msg });
      if (questionIndex === questions.length - 1) {
        setLastQuestion(true);
      }
    } else {
      setIncorrectAnswer(true);
      const count = incorrectAnswerCount + 1;

      if (count >= 2) {
        if (questionIndex === questions.length - 1) {
          setLastQuestion(true);
        }
      }
      setIncorrectAnswerCount(count);

      setAnswerResponse({
        case: count >= 2 ? 'incorrect' : 'hint',
        message:
          count >= 2 ? question.incorrect_msg_two : question.incorrect_msg,
      });
    }
  };

  const onNextQuestion = () => {
    if (lastQuestion) {
      handleSubmit();
    } else {
      const nextIndex = questionIndex + 1;
      if (nextIndex === questions.length - 1) {
        setLastQuestion(true);
      }

      setQuestionIndex(nextIndex);
      resetAnswerChecks();
    }

    scrollContainer.current.scrollTop = 0;
    setIsScrolled(false);
  };

  const resetAnswerChecks = () => {
    setCorrectAnswer(false);
    setIncorrectAnswer(false);
    setIncorrectAnswerCount(0);
  };

  const handleAnswerSubmit = () => {
    if (correctAnswer || incorrectAnswerCount >= 2) {
      setCorrectAnswer(false);
      setIncorrectAnswer(false);
      setIncorrectAnswerCount(0);
      onNextQuestion();
      setAnswerResponse(null);
    } else if (incorrectAnswer) {
      scrollContainer.current.scrollTop = 0;
      setIncorrectAnswer(false);
      setAnswerResponse(null);
      resetAnswersByQuestion(); //Reset selected answers by the user.
    } else {
      scrollContainer.current.scrollTop = 0;
      checkUserAnswer();
    }
  };

  const setStudentAnswer = () => {
    const question = questions[questionIndex];
    if (studentAnswers[question.id] && studentAnswers[question.id].length) {
      return;
    } else {
      if (question.type === 'SEQUENCE') {
        handleQuestionChange(question.answers.map((ans) => ans.id));
      }
    }
  };

  const handleQuestionChange = (userAnswer) => {
    const answers = clone(studentAnswers);
    const question = questions[questionIndex];
    answers[question.id] = userAnswer;
    setStudentAnswers(answers);
  };

  function shuffleSequenceAnswers(answers) {
    for (let i = answers.length - 1; i > 0; i--) {
        let j = Math.floor(Math.random() * (i + 1));
        let temp = answers[i];
        answers[i] = answers[j];
        answers[j] = temp;
    }

    return answers;
  }

  const resetAnswersByQuestion = () => {
    const answers = clone(studentAnswers);
    const question = questions[questionIndex];
    if (question.type === 'SEQUENCE') {
      answers[question.id] = shuffleSequenceAnswers(question.answers.map((ans) => ans.id));
    } else {
      answers[question.id] = [];
    }
    setStudentAnswers(answers);
  }

  const getStudentResults = (question) => {
    const isCorrect = checkAnswer(question, studentAnswers[question.id]);
    const answer = studentAnswers[question.id] ? studentAnswers[question.id] : [];
    const studentAnswer = JSON.stringify(removeTextFromUserAnswer(question.type, answer));

    return {
      result: isCorrect,
      question_id: question.id,
      user_id: viewer?.id,
      study_project_id: projectId,
      group_id: groupId,
      answer: studentAnswer,
    };
  };

  const handleSubmit = async () => {
    const results = questions.map(getStudentResults);
    try {
      if (!preview) {
        await createResults({
          variables: { data: results },
          refetchQueries: [
            {
              query: GET_STUDENT_COURSE_PROGRESS,
              variables: { courseId, studentId: viewer.id, groupId },
            },
          ],
        });

        handleCreateResultHistory(results);
      }

      setStudentResults(results);
      setShowAssessment(false);
      setShowResults(true);

    } catch (error) {
      enqueueSnackbar('Oops! Something went wrong!', { variant: 'error' });
    }
  };

  const handleOnSaveProgress = () => {
    const questionsId = questions.map((question) => {
      return question.id
    });

    createStudyProjectProgressDraft({
      variables: {
        data: {
          user_id: viewer.id,
          question_index: questionIndex,
          study_project_id: projectId,
          group_id: groupId,
          progress: JSON.stringify(studentAnswers),
          questions: JSON.stringify(questionsId)
        }
      }
    })
  }

  const handleCreateResultHistory = async (results) => {
    const data = {
      type: 'STUDY_PROJECT',
      exercise_id: projectId,
      group_id: groupId,
      corrects: results.reduce((acc, result) => {
        if (result.result === true) {
          acc++;
        }
        return acc;
      }, 0),
      incorrects: results.reduce((acc, result) => {
        if (result.result === false) {
          acc++;
        }
        return acc;
      }, 0),
      user_id: viewer.id
    }

    await createResultHistory({
      variables: {
        data: data
      }
    });
  }

  const handleClose = () => {
    showConfirmation({
      title: 'Exit Study Project?',
      message:
        'If you exit now, your progress will not be saved. Are you sure you want to exit?',
      confirmed: () => history.goBack(),
      confirmText: 'Exit',
    });
  };

  const handleOnScrollProjectContainer = () => {
    if (scrollContainer.current.scrollTop === 0) {
      setIsScrolled(false);
    } else {
      setIsScrolled(true);
    }
  }

  // const getCurrentCaseVariant = () => {
  //   const question = questions[questionIndex];
  //   return question.isScenario ? 'Scenario' : 'Reinforced';
  // };

  if (loading)
    return (
      <LoadingScreen>
        <Typography align="center" variant="body2">
          Fetching Study Project...
        </Typography>
      </LoadingScreen>
    );
  if (error) return <p>Error...</p>;
  
  return (
    <section ref={scrollContainer} onScroll={handleOnScrollProjectContainer} className={classes.projectSection}>
      { 
        scrollDownNotification ?       
          <Box style={{
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            position: 'fixed',
            width: '100%',
            zIndex: 2,
            bottom: '1em'}}>
            <svg width="140" height="51" viewBox="0 0 140 51" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="140" height="51" rx="25.5" fill="#3FAE2A" fillOpacity="0.4"/>
              <path d="M60.1081 32.364C59.6063 31.8584 58.8152 31.8825 58.3412 32.4178C57.8672 32.9531 57.8898 33.797 58.3917 34.3026L69.6409 45.636C70.1225 46.1213 70.8755 46.1213 71.3573 45.6361L82.6082 34.3027C83.1101 33.7971 83.1328 32.9533 82.6588 32.4179C82.1849 31.8825 81.3938 31.8584 80.8919 32.3639L70.4992 42.8328L60.1081 32.364Z" fill="#4F4F4F" fillOpacity="0.6"/>
              <path d="M38.3018 17.3408C38.3018 18.2204 37.9827 18.9062 37.3447 19.3984C36.7067 19.8906 35.8408 20.1367 34.7471 20.1367C33.5622 20.1367 32.6507 19.984 32.0127 19.6787V18.5576C32.4229 18.7308 32.8695 18.8675 33.3525 18.9678C33.8356 19.068 34.3141 19.1182 34.7881 19.1182C35.5628 19.1182 36.1462 18.9723 36.5381 18.6807C36.93 18.3844 37.126 17.9743 37.126 17.4502C37.126 17.1038 37.0553 16.8213 36.9141 16.6025C36.7773 16.3792 36.5449 16.1742 36.2168 15.9873C35.8932 15.8005 35.3988 15.5885 34.7334 15.3516C33.8037 15.0189 33.1383 14.6247 32.7373 14.1689C32.3408 13.7132 32.1426 13.1185 32.1426 12.3848C32.1426 11.6146 32.432 11.0016 33.0107 10.5459C33.5895 10.0902 34.3551 9.8623 35.3076 9.8623C36.3011 9.8623 37.2148 10.0446 38.0488 10.4092L37.6865 11.4209C36.8617 11.0745 36.0596 10.9014 35.2803 10.9014C34.665 10.9014 34.1842 11.0335 33.8379 11.2979C33.4915 11.5622 33.3184 11.929 33.3184 12.3984C33.3184 12.7448 33.3822 13.0296 33.5098 13.2529C33.6374 13.4717 33.8516 13.6745 34.1523 13.8613C34.4577 14.0436 34.9225 14.2464 35.5469 14.4697C36.5951 14.8434 37.3151 15.2445 37.707 15.6729C38.1035 16.1012 38.3018 16.6572 38.3018 17.3408ZM43.1689 20.1367C42.0843 20.1367 41.2435 19.804 40.6465 19.1387C40.054 18.4688 39.7578 17.5231 39.7578 16.3018C39.7578 15.0485 40.0586 14.0801 40.6602 13.3965C41.2663 12.7129 42.1276 12.3711 43.2441 12.3711C43.6042 12.3711 43.9642 12.4098 44.3242 12.4873C44.6842 12.5648 44.9668 12.6559 45.1719 12.7607L44.8232 13.7246C44.5726 13.6243 44.2992 13.5423 44.0029 13.4785C43.7067 13.4102 43.4447 13.376 43.2168 13.376C41.6947 13.376 40.9336 14.3467 40.9336 16.2881C40.9336 17.2087 41.1182 17.915 41.4873 18.4072C41.861 18.8994 42.4124 19.1455 43.1416 19.1455C43.766 19.1455 44.4062 19.0111 45.0625 18.7422V19.7471C44.5612 20.0068 43.93 20.1367 43.1689 20.1367ZM50.2646 12.3711C50.5973 12.3711 50.8958 12.3984 51.1602 12.4531L51.0029 13.5059C50.693 13.4375 50.4196 13.4033 50.1826 13.4033C49.5765 13.4033 49.057 13.6494 48.624 14.1416C48.1956 14.6338 47.9814 15.2467 47.9814 15.9805V20H46.8467V12.5078H47.7832L47.9131 13.8955H47.9678C48.2458 13.4079 48.5807 13.0319 48.9727 12.7676C49.3646 12.5033 49.7952 12.3711 50.2646 12.3711ZM59.0283 16.2471C59.0283 17.4684 58.7207 18.4232 58.1055 19.1113C57.4902 19.7949 56.6403 20.1367 55.5557 20.1367C54.8857 20.1367 54.291 19.9795 53.7715 19.665C53.252 19.3506 52.8509 18.8994 52.5684 18.3115C52.2858 17.7236 52.1445 17.0355 52.1445 16.2471C52.1445 15.0257 52.4499 14.0755 53.0605 13.3965C53.6712 12.7129 54.5189 12.3711 55.6035 12.3711C56.6517 12.3711 57.4834 12.7197 58.0986 13.417C58.7184 14.1143 59.0283 15.0576 59.0283 16.2471ZM53.3203 16.2471C53.3203 17.2041 53.5117 17.9333 53.8945 18.4346C54.2773 18.9359 54.8402 19.1865 55.583 19.1865C56.3258 19.1865 56.8887 18.9382 57.2715 18.4414C57.6589 17.9401 57.8525 17.2087 57.8525 16.2471C57.8525 15.2946 57.6589 14.5723 57.2715 14.0801C56.8887 13.5833 56.3213 13.335 55.5693 13.335C54.8265 13.335 54.266 13.5788 53.8877 14.0664C53.5094 14.554 53.3203 15.2809 53.3203 16.2471ZM62.1592 20H61.0244V9.36328H62.1592V20ZM65.7002 20H64.5654V9.36328H65.7002V20ZM79.8916 14.9072C79.8916 16.557 79.4427 17.8193 78.5449 18.6943C77.6517 19.5648 76.3643 20 74.6826 20H71.9141V10.0059H74.9766C76.5306 10.0059 77.7383 10.4365 78.5996 11.2979C79.4609 12.1592 79.8916 13.3623 79.8916 14.9072ZM78.6611 14.9482C78.6611 13.6449 78.333 12.6628 77.6768 12.002C77.0251 11.3411 76.0544 11.0107 74.7646 11.0107H73.0762V18.9951H74.4912C75.8766 18.9951 76.918 18.6556 77.6152 17.9766C78.3125 17.293 78.6611 16.2835 78.6611 14.9482ZM88.4229 16.2471C88.4229 17.4684 88.1152 18.4232 87.5 19.1113C86.8848 19.7949 86.0348 20.1367 84.9502 20.1367C84.2803 20.1367 83.6855 19.9795 83.166 19.665C82.6465 19.3506 82.2454 18.8994 81.9629 18.3115C81.6803 17.7236 81.5391 17.0355 81.5391 16.2471C81.5391 15.0257 81.8444 14.0755 82.4551 13.3965C83.0658 12.7129 83.9134 12.3711 84.998 12.3711C86.0462 12.3711 86.8779 12.7197 87.4932 13.417C88.113 14.1143 88.4229 15.0576 88.4229 16.2471ZM82.7148 16.2471C82.7148 17.2041 82.9062 17.9333 83.2891 18.4346C83.6719 18.9359 84.2347 19.1865 84.9775 19.1865C85.7204 19.1865 86.2832 18.9382 86.666 18.4414C87.0534 17.9401 87.2471 17.2087 87.2471 16.2471C87.2471 15.2946 87.0534 14.5723 86.666 14.0801C86.2832 13.5833 85.7158 13.335 84.9639 13.335C84.221 13.335 83.6605 13.5788 83.2822 14.0664C82.904 14.554 82.7148 15.2809 82.7148 16.2471ZM96.5371 20L95.1631 15.6045C95.0765 15.3356 94.9147 14.7249 94.6777 13.7725H94.623C94.4408 14.57 94.2812 15.1852 94.1445 15.6182L92.7295 20H91.417L89.373 12.5078H90.5625C91.0456 14.39 91.4124 15.8232 91.6631 16.8076C91.9183 17.792 92.0641 18.4551 92.1006 18.7969H92.1553C92.2054 18.5371 92.2852 18.2021 92.3945 17.792C92.5085 17.3773 92.6064 17.0492 92.6885 16.8076L94.0625 12.5078H95.293L96.6328 16.8076C96.888 17.5915 97.0612 18.25 97.1523 18.7832H97.207C97.2253 18.6191 97.2731 18.3662 97.3506 18.0244C97.4326 17.6826 97.9089 15.8438 98.7793 12.5078H99.9551L97.8838 20H96.5371ZM106.442 20V15.1533C106.442 14.5426 106.303 14.0869 106.025 13.7861C105.747 13.4854 105.312 13.335 104.72 13.335C103.936 13.335 103.362 13.5469 102.997 13.9707C102.632 14.3945 102.45 15.0941 102.45 16.0693V20H101.315V12.5078H102.238L102.423 13.5332H102.478C102.71 13.1641 103.036 12.8792 103.455 12.6787C103.874 12.4736 104.341 12.3711 104.856 12.3711C105.759 12.3711 106.438 12.5898 106.894 13.0273C107.349 13.4603 107.577 14.1553 107.577 15.1123V20H106.442Z" fill="#4F4F4F" fillOpacity="0.6"/>
            </svg>
          </Box> : null
      }
      <div className={classes.projectContainer}>
        {start && !showResults && !showComplete && (
          <Fragment>
            <header className={classes.header}>
              <Toolbar className={classes.toolbar}>
                <Box>
                  <div
                    className={classes.imageContainer}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    <img
                      alt={project.lesson.name}
                      src={project.lesson.logo}
                      width={50}
                      height={50}
                    />
                    <div className={classes.header_title}>
                      <Typography>
                        Level {project.level}
                      </Typography>
                      <Typography
                        style={{
                          fontWeight: 'bold',
                          fontSize: '25px',
                        }}
                      >
                        {project.lesson.name}
                      </Typography>
                      {start && showAssessment && (
                        <Box
                          alignSelf="stretch"
                          display="flex"
                          flexDirection="column"
                          justifyContent="center"
                          pl={0}
                          pr={0}
                        >
                          <Typography className={classes.questionTitle}>
                            Study Project {studyProjectNumber} - Case Study {caseStudyIndexes[questions[questionIndex].id]}
                          </Typography>
                        </Box>
                      )}
                    </div>
                  </div>
                </Box>
                <div className={classes.header_close}>
                  {start && showAssessment && (
                    <Box mr={1} >
                      <Typography className={classes.projectTitle}>
                        QUESTION {`${questionIndex + 1} OF ${questions.length}`}
                      </Typography>
                    </Box>
                  )}
                  {start && showIntro && (
                    <Box mr={1} >
                      <Typography className={classes.projectTitle}>
                        Study Project{' '}
                        {studyProjectNumber ? `#${studyProjectNumber}` : ''}
                      </Typography>
                    </Box>
                  )}
                  <CloseButton
                    iconProps={{ fontSize: 'large', style: {color: '#FFFFFF'} }}
                    onClick={() => handleClose()}
                  />
                </div>
              </Toolbar>
            </header>
          </Fragment>
        )}
        <Container className={classes.mainContainer}>
          {start && showIntro && (
            <Intro
              project={project}
              courseId={courseId}
              onClick={handleOnShowAssessment}
            />
          )}
          {start && showAssessment && (
            <Fragment>
              <Assessment
                showAnswer={incorrectAnswerCount >= 2}
                onQuestionChange={handleQuestionChange}
                userAnswers={studentAnswers}
                question={questions[questionIndex]}
              >
                <AnswerResponse
                  response={answerResponse}
                  question={questions[questionIndex]}
                  caseStudyNumber={caseStudyIndexes[questions[questionIndex].id]}
                  courseId={courseId}
                  match={match}
                  lesson={project.lesson}
                />
              </Assessment>
              <Box className={classes.answerResponseContainer}>
                <Modal
                open={openSaveProgressModal}>
                  <Box className={classes.modalSuccessSaveProgress}>
                    <Card className={classes.cardSuccessSaveProgress}>
                      <Box>
                        <Typography variant='h4' >Your progress has been successfully saved!</Typography>
                        <Typography variant='subtitle1' >Do you want to exit and continue later?</Typography>
                      </Box>
                      <Box className={classes.cardSuccessSaveProgressButtons}>
                        <Button onClick={() => setOpenSaveProgressModal(false)} color='primary' variant='outlined' >continue</Button>
                        <Button onClick={() => history.push(`/group/${groupId}/course/${courseId}`)} color='primary' variant='contained' >Exit and continue later</Button>
                      </Box>
                    </Card>
                  </Box>
                </Modal>
                <AnswerBar
                  onClick={() => {
                    setShowIntro(true);
                    setShowAssessment(false);
                  }}
                  onSaveProgress={handleOnSaveProgress}
                  lastQuestion={questions.length === 1 ? true : lastQuestion}
                  onSubmit={handleAnswerSubmit}
                  correctAnswer={correctAnswer}
                  incorrectAnswer={incorrectAnswer}
                  viewer={viewer}
                  preview={preview}
                  incorrectAnswerCount={incorrectAnswerCount}
                />
              </Box>
            </Fragment>
          )}
          {start && showResults && (
            <Results
              studentResults={studentResults}
              project={project}
              onClick={() => {
                setShowResults(false);
                setShowComplete(true);
              }}
              tryAgainOnClick={() => window.location.reload()}
              studyProjectNumber={studyProjectNumber}
            />
          )}           
        </Container>
        {!start && (
          <Start
            onClose={() => history.goBack()}
            onStart={() => {
              setStart(true);
              setShowIntro(true);
            }}
            preview={preview}
            project={project}
            projectNumber={studyProjectNumber}
            studentAnswers={studentAnswers}
          />
        )}
        {start && showComplete && (
          <StudyProjectComplete
            preview={preview}
            onClick={
              preview
                ? () => history.goBack()
                : () => history.push(`/group/${groupId}/course/${courseId}`)
            }/>
        )}              
      </div>
    </section>
  );
};

export default withSnackbar(StudyProject);
